// Site-wide tracking: Google Tag Manager, TruConversion and dataLayer events.
// Injected at runtime because this project has no editable index.html.

type DataLayerEvent = Record<string, unknown>

declare global {
  interface Window {
    dataLayer: DataLayerEvent[]
    _tip: unknown[]
    __adroiTrackingReady?: boolean
  }
}

const GTM_ID = 'GTM-MRGFBF7'

// Third-party tags only load on the live domain. TruConversion's script is locked to
// adroimarketing.com and throws cross-origin errors ("Script error.") in the editor preview.
// Add any other live hostnames here.
const TRACKING_HOSTS = ['adroimarketing.com']

function isLiveHost() {
  const host = window.location.hostname
  return TRACKING_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))
}
const TRUCONVERSION_SRC = '//app.truconversion.com/ti-js/34153/5e1e2.js'

const CTA_PATTERN =
  /^(contact us|schedule|book|get started|talk to us|see it in action|get free marketing audit|get the .+ playbook|find your industry)/i

function currentPath() {
  return window.location.pathname + window.location.hash
}

function push(event: DataLayerEvent) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(event)
}

function loadGoogleTagManager() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.insertBefore(script, document.head.firstChild)

  // noscript fallback, placed first in <body>
  const noscript = document.createElement('noscript')
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
  document.body.insertBefore(noscript, document.body.firstChild)
}

function loadTruConversion() {
  window._tip = window._tip || []
  if (document.getElementById('ti-js')) return
  const script = document.createElement('script')
  script.id = 'ti-js'
  script.async = true
  script.src = document.location.protocol + TRUCONVERSION_SRC
  document.head.appendChild(script)
}

function handleClick(e: MouseEvent) {
  const target = e.target as Element | null
  const el = target?.closest('a, button') as HTMLAnchorElement | HTMLButtonElement | null
  if (!el) return

  const href = el instanceof HTMLAnchorElement ? el.getAttribute('href') ?? '' : ''

  if (href.startsWith('tel:')) {
    push({ event: 'phone_click', phone_number: href, page_path: currentPath() })
    return
  }
  if (href.startsWith('mailto:')) {
    push({ event: 'email_click', page_path: currentPath() })
  }

  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  if (el.hasAttribute('data-cta') || CTA_PATTERN.test(text)) {
    push({ event: 'cta_click', cta_text: text, page_path: currentPath() })
  }
}

/** Push a virtual page view. Call after the page title has been updated. */
export function trackPageView() {
  push({ event: 'virtual_page_view', page_path: currentPath(), page_title: document.title })
}

/** Push a lead conversion — call once when a form's thank-you state appears. */
export function trackLead(formName: string) {
  push({ event: 'generate_lead', form_name: formName, page_path: currentPath() })
}

export function initTracking() {
  if (window.__adroiTrackingReady) return
  window.__adroiTrackingReady = true

  window.dataLayer = window.dataLayer || []
  if (isLiveHost()) {
    try {
      loadGoogleTagManager()
      loadTruConversion()
    } catch (err) {
      console.warn('[tracking] Failed to load third-party tags', err)
    }
  }
  document.addEventListener('click', handleClick, true)

  // Hash changes that stay on the same page (e.g. in-page anchors) still count as a view.
  // Deferred so the route's title is applied first.
  window.addEventListener('hashchange', () => window.setTimeout(trackPageView, 0))
}
